import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import useCurrentPage from '../../../hooks/useCurrentPage';
import FacilityLink from '../FacilityLink';
import FacilitiesLegendItemMarker from '../FacilitiesLegendItemMarker';
import FacilityPin from '../../stateful/Map/FacilityPin';
import styles from './styles.scss';
import MapPin from '../../stateful/Map/MapPin';

export const DISPLAY_OPTION_SIMPLE = 'simple';
export const DISPLAY_OPTION_PIN = 'pin';
export const DISPLAY_OPTION_DEFAULT = DISPLAY_OPTION_SIMPLE;

export const DISPLAY_OPTIONS = [
  DISPLAY_OPTION_SIMPLE,
  DISPLAY_OPTION_PIN,
];

const propTypes = {
  disableLink: PropTypes.bool,
  displayOption: PropTypes.oneOf(DISPLAY_OPTIONS),
  facility: PropTypes.shape({
    address: PropTypes.shape({
      address1: PropTypes.string.isRequired,
      address2: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      postal: PropTypes.string.isRequired,
    }).isRequired,
    displayName: PropTypes.string,
    settings: PropTypes.shape({
      businessAddressDescription: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onHover: PropTypes.func,
  primary: PropTypes.bool,
};

const defaultProps = {
  disableLink: false,
  displayOption: DISPLAY_OPTION_DEFAULT,
  onHover: undefined,
  primary: undefined,
};

const FacilitiesLegendItem = ({
  disableLink,
  displayOption,
  facility,
  index,
  onHover,
  primary,
}) => {
  const { primaryColorClass } = useCurrentPage();
  const currentPage = useCurrentPage();

  const { address, displayName, settings, id } = facility;

  let marker;

  if (displayOption === DISPLAY_OPTION_SIMPLE) {
    marker = (
      <FacilitiesLegendItemMarker index={index} />
    );
  } else if (displayOption === DISPLAY_OPTION_PIN) {
    const MapPinComponent = primary ? FacilityPin : MapPin;

    marker = (
      <MapPinComponent
        className={styles.pin}
        id={facility.id}
        index={id}
        color={currentPage.primaryColor}
      />
    );
  }

  const onMouseLeave = onHover ? () => onHover() : undefined;
  const onMouseEnter = onHover ? () => onHover(facility) : undefined;

  const linkClasses = cx(
    styles.name,
    primaryColorClass,
  );

  return (
    <li
      className={styles.item}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
    >
      {marker}
      <div className={styles.info}>
        {disableLink ? (
          <div
            className={linkClasses}
            data-testid="facilities-legend-item-name"
          >
            {displayName}
          </div>
        ) : (
          <FacilityLink
            className={linkClasses}
            data-testid="facilities-legend-item-name"
            facility={facility}
          >
            {displayName}
          </FacilityLink>
        )}
        <div className={styles.modifier}>{settings.businessAddressDescription}</div>
        <div className={styles.address}>{address.address1}</div>
        <div className={styles.address}>{address.address2}</div>
        <div className={styles.address}>{`${address.city}, ${address.state} ${address.postal}`}</div>
      </div>
    </li>
  );
};

FacilitiesLegendItem.propTypes = propTypes;
FacilitiesLegendItem.defaultProps = defaultProps;

FacilitiesLegendItem.fragments = {
  facility: gql`
    fragment FacilitiesLegendItemFacility on Facility {
      id
      address {
        address1
        address2
        city
        state
        postal
      }
      displayName
      settings {
        businessAddressDescription
      }
      ...FacilityLinkFacility

    }

    ${FacilityLink.fragments.facility}
  `,
};

export default FacilitiesLegendItem;
